import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApi } from '../../../../hooks/useApi';
import {
  triggerRealizationSync,
  getRealizationSyncStatus,
  resetRealizationMonth,
  resetAllRealizationMonths,
  getRealizationAnalytics,
  listRealizationPostings,
  getCustomerOrders,
} from '../../../../services/ozonRealization';
import { useOzonChat, ChatWidget } from '../shared/CustomerChatModal';
import { filterBySearch } from '../../../../utils/search';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const REPORT_TABS = [
  { key: 'postings',      label: 'Заказы' },
  { key: 'summary',       label: 'Сводка' },
  { key: 'dynamics',      label: 'Динамика' },
  { key: 'segments',      label: 'Сегменты' },
  { key: 'products',      label: 'Ассортимент' },
  { key: 'first_product', label: 'Первый товар' },
  { key: 'customers',     label: 'Клиенты' },
  { key: 'paths',         label: 'Пути' },
];

const TAB_DESCRIPTIONS = {
  postings: 'Сырые строки реализации из Ozon. Одна строка = один SKU в одном заказе.',
  summary: 'Итоговые показатели за весь период: покупатели, заказо-дни, выручка, доля повторных.',
  dynamics: 'Помесячная динамика: сколько новых и повторных покупателей каждый месяц, их ABC-сегменты. Новый = первая покупка в этом месяце. ABC: A = 4+ заказо-дней за всё время, B = 2–3, C = 1.',
  segments: 'Вся база покупателей разбита по числу заказо-дней (1, 2–3, 4–6, 7–10, 11–15, 16+). Показывает выручку от каждого сегмента.',
  products: 'По каждому товару: сколько покупателей возвращались после первой покупки этого товара и куда шли дальше. Себя — снова купили этот товар. Кросс — купили другой. Оба — и то и другое. ABCD — доля в суммарной выручке (A = топ 80%).',
  first_product: 'Какой товар был первым в истории покупателя? После него покупатели возвращались? Куда уходили?',
  customers: 'Список всех повторных покупателей (≥2 заказо-дней) с количеством дней покупок и суммами.',
  paths: 'Последовательность главных товаров по датам у повторных покупателей. Показывает типичные сценарии потребления.',
};

const STATUS_COLOR = { ok: '#27ae60', error: '#e74c3c', pending: '#f39c12' };
// Letter text colors
const ABCD_COLOR   = { A: '#1e8449', B: '#b7770d', C: '#999', D: '#c0392b' };
// Cell background tints
const ABCD_CELL_BG = { A: 'rgba(39,174,96,0.13)', B: 'rgba(243,156,18,0.10)', C: 'transparent', D: 'rgba(231,76,60,0.10)' };

// Standard ABCD (Pareto cumulative):
//   A — items forming top 80% of total value
//   B — next 15% (cumulative 95%)
//   C — all remaining items with value > 0
//   D — items with value = 0 (non-contributing)
function abcdByCumulative(items, getValue) {
  const total = items.reduce((s, r) => s + (getValue(r) || 0), 0);
  if (!total) return () => 'D';
  const sorted = [...items].sort((a, b) => (getValue(b) || 0) - (getValue(a) || 0));
  const map = new Map();
  let cum = 0;
  for (const item of sorted) {
    const v = getValue(item) || 0;
    if (v <= 0) { map.set(item, 'D'); continue; }
    cum += v;
    const share = cum / total;
    map.set(item, share <= 0.80 ? 'A' : share <= 0.95 ? 'B' : 'C');
  }
  return (item) => map.get(item) ?? 'D';
}

const abcdBadge = (letter) =>
  letter
    ? <span style={{ fontWeight: 700, color: ABCD_COLOR[letter] }}>{letter}</span>
    : <span style={{ color: '#ddd' }}>—</span>;

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

const pct  = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '—';
const rub  = (v) => v != null ? Math.round(v).toLocaleString('ru-RU') + ' ₽' : '—';
const num  = (v) => v != null ? Number(v).toLocaleString('ru-RU') : '—';

function csvVal(v) {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return `"${s}"`;
}

function downloadCsv(cols, rows, filename) {
  const header = cols.map((c) => csvVal(c.label)).join(',');
  const lines = rows.map((r) =>
    cols.map((c) => {
      const raw = c.csvRender ? c.csvRender(r) : (r[c.csvKey || c.key] ?? '');
      return csvVal(raw);
    }).join(',')
  );
  const csv = '﻿' + [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableTable
// ─────────────────────────────────────────────────────────────────────────────

function SortableTable({ cols, rows, emptyText = 'Нет данных', filename = 'export.csv', pageSize = null }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page,    setPage]    = useState(1);

  const sorted = useMemo(() => {
    if (!rows) return [];
    if (!sortKey) return rows;
    const sk = sortKey;
    return [...rows].sort((a, b) => {
      const av = a[sk];
      const bv = b[sk];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv
        : String(av).localeCompare(String(bv), 'ru');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  // reset to page 1 on sort or filter change
  useEffect(() => { setPage(1); }, [sortKey, sortDir, rows]);

  const totalPages = pageSize ? Math.ceil(sorted.length / pageSize) : 1;
  const visible   = pageSize ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (!rows || !rows.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{emptyText}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
        {pageSize && totalPages > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <button className="btn btn--sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span style={{ color: 'var(--text-muted)' }}>{page} / {totalPages} ({sorted.length})</span>
            <button className="btn btn--sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        ) : <span />}
        <button className="btn btn--sm" onClick={() => downloadCsv(cols, sorted, filename)} style={{ fontSize: 12 }}>↓ CSV</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {cols.map((c) => {
                const sk = c.sortKey || c.key;
                const active = sortKey === sk;
                return (
                  <th key={c.key}
                    onClick={() => c.noSort ? null : toggleSort(sk)}
                    style={{
                      padding: '6px 10px',
                      textAlign: c.right ? 'right' : 'left',
                      background: 'var(--glass-80, #f0f2f5)',
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--border)',
                      cursor: c.noSort ? 'default' : 'pointer',
                      userSelect: 'none',
                      color: active ? 'var(--primary, #2980b9)' : undefined,
                    }}>
                    {c.tooltip
                      ? <span title={c.tooltip} style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{c.label}</span>
                      : c.label}
                    {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                {cols.map((c) => (
                  <td key={c.key}
                    style={{ padding: '5px 10px', textAlign: c.right ? 'right' : 'left',
                      whiteSpace: c.wrap ? 'normal' : 'nowrap', maxWidth: c.wrap ? 300 : undefined,
                      ...(c.cellStyle ? c.cellStyle(r) : {}) }}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageSize && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }}>
          <button className="btn btn--sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="btn btn--sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
          <span style={{ color: 'var(--text-muted)' }}>Страница {page} из {totalPages}</span>
          <button className="btn btn--sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          <button className="btn btn--sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TabDesc — collapsible description
// ─────────────────────────────────────────────────────────────────────────────

function TabDesc({ tabKey }) {
  const [open, setOpen] = useState(false);
  const text = TAB_DESCRIPTIONS[tabKey];
  if (!text) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <button type="button"
        style={{ background: 'none', border: 'none', color: 'var(--link, #2980b9)', cursor: 'pointer', fontSize: 12, padding: 0 }}
        onClick={() => setOpen((v) => !v)}>
        {open ? '▲ Скрыть описание' : '▼ Что показывает этот отчёт?'}
      </button>
      {open && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', maxWidth: 700 }}>{text}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Report tabs
// ─────────────────────────────────────────────────────────────────────────────

function Metric({ label, value, sub }) {
  return (
    <div style={{ background: 'var(--glass-80, #f5f7fa)', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SummaryTab({ data }) {
  if (!data) return null;
  return (
    <>
      <TabDesc tabKey="summary" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Metric label="Покупателей всего"       value={num(data.total_buyers)} />
        <Metric label="Заказо-дней"             value={num(data.total_order_days)} />
        <Metric label="Строк реализации"        value={num(data.total_rows)} />
        <Metric label="Выручка"                 value={rub(data.total_revenue)} />
        <Metric label="Реализация (факт)"       value={rub(data.total_realization)} />
        <Metric label="Ср. чек / заказо-день"   value={rub(data.avg_revenue_per_order_day)} />
        <Metric label="Повторники"              value={num(data.repeat_buyers)} sub={pct(data.repeat_buyers_pct) + ' от всех'} />
        <Metric label="Разовые покупатели"      value={num(data.one_time_buyers)} />
        <Metric label="Выручка разовых"         value={rub(data.one_time_revenue)} />
      </div>
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--glass-80)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', maxWidth: 600 }}>
        <strong>Заказо-день</strong> — уникальная пара «покупатель + дата покупки». Если покупатель купил 3 товара в один день — это 1 заказо-день, но может быть 3 строки реализации.<br />
        <strong>Строка реализации</strong> — одна позиция в одном заказе (один SKU). Несколько строк в один день = несколько SKU или несколько заказов в тот же день.
      </div>
    </>
  );
}

function DynamicsTab({ dynamics, daily }) {
  const [view, setView] = useState('monthly');

  const dynCols = [
    { key: 'month',               label: 'Месяц',        sortKey: 'month' },
    { key: 'revenue',             label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),            csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка за месяц по всем заказам в реализации' },
    { key: 'realization',         label: 'Реализация ₽', right: true, render: (r) => rub(r.realization),        csvRender: (r) => Math.round(r.realization),
      tooltip: 'Фактическая реализация: выручка за вычетом возвратов и корректировок' },
    { key: 'orders_by_days',      label: 'Заказо-дней',  right: true, render: (r) => num(r.orders_by_days),
      tooltip: 'Заказо-день = уникальная пара «покупатель + дата покупки». Исключает двойной счёт при нескольких товарах в один день' },
    { key: 'customers_total',     label: 'Покупателей',  right: true, render: (r) => num(r.customers_total),
      tooltip: 'Уникальных покупателей, совершивших хотя бы одну покупку в этом месяце' },
    { key: 'new_customers',       label: 'Новых',        right: true, render: (r) => `${num(r.new_customers)} (${pct(r.new_share)})`,       csvRender: (r) => r.new_customers,
      tooltip: 'Покупатели, чья первая покупка в системе произошла в этом месяце. В скобках — % от всех покупателей месяца' },
    { key: 'returning_customers', label: 'Повторных',    right: true, render: (r) => `${num(r.returning_customers)} (${pct(r.returning_share)})`, csvRender: (r) => r.returning_customers,
      tooltip: 'Покупатели с историей покупок в предыдущих месяцах. В скобках — % от всех покупателей месяца' },
    { key: 'a_count', label: 'A (4+)',  right: true, render: (r) => `${r.a_count} (${pct(r.a_share)})`, csvRender: (r) => r.a_count,
      tooltip: 'Активные покупатели: 4 и более уникальных дня покупок в этом месяце' },
    { key: 'b_count', label: 'B (2–3)', right: true, render: (r) => `${r.b_count} (${pct(r.b_share)})`, csvRender: (r) => r.b_count,
      tooltip: 'Регулярные покупатели: 2–3 уникальных дня покупок в этом месяце' },
    { key: 'c_count', label: 'C (1)',   right: true, render: (r) => `${r.c_count} (${pct(r.c_share)})`, csvRender: (r) => r.c_count,
      tooltip: 'Разовые покупатели: ровно 1 день покупок в этом месяце' },
  ];

  const dailyCols = [
    { key: 'date',        label: 'Дата' },
    { key: 'revenue',     label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),      csvRender: (r) => Math.round(r.revenue) },
    { key: 'realization', label: 'Реализация ₽', right: true, render: (r) => rub(r.realization),  csvRender: (r) => Math.round(r.realization) },
    { key: 'order_days',  label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days) },
    { key: 'buyers',      label: 'Покупателей',  right: true, render: (r) => num(r.buyers) },
    { key: 'rows',        label: 'Строк',        right: true, render: (r) => num(r.rows) },
  ];

  const dynReversed = useMemo(() => [...(dynamics || [])].reverse(), [dynamics]);

  return (
    <>
      <TabDesc tabKey="dynamics" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className={`btn btn--sm${view === 'monthly' ? ' btn--primary' : ''}`} onClick={() => setView('monthly')}>По месяцам</button>
        <button className={`btn btn--sm${view === 'daily'   ? ' btn--primary' : ''}`} onClick={() => setView('daily')}>По дням</button>
      </div>
      {view === 'monthly' && <SortableTable cols={dynCols} rows={dynReversed} filename="dynamics_monthly.csv" />}
      {view === 'daily'   && <SortableTable cols={dailyCols} rows={daily || []} filename="dynamics_daily.csv" />}
    </>
  );
}

function SegmentsTab({ data }) {
  const cols = [
    { key: 'segment',           label: 'Сегмент',
      tooltip: 'Сегмент по числу уникальных дней покупок за весь период: 1, 2–3, 4–6, 7–10, 11–15, 16+' },
    { key: 'customers',         label: 'Покупателей',   right: true, render: (r) => num(r.customers),
      tooltip: 'Число уникальных покупателей в этом сегменте' },
    { key: 'order_days',        label: 'Заказо-дней',   right: true, render: (r) => num(r.order_days),
      tooltip: 'Суммарное число заказо-дней всех покупателей сегмента' },
    { key: 'revenue',           label: 'Выручка ₽',     right: true, render: (r) => rub(r.revenue),     csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка покупателей этого сегмента' },
    { key: 'revenue_share',     label: '% выручки',     right: true, render: (r) => pct(r.revenue_share),       csvRender: (r) => (r.revenue_share * 100).toFixed(1),
      tooltip: 'Доля выручки сегмента от общей. Лояльные покупатели (16+ дней) обычно формируют непропорционально высокую долю оборота' },
    { key: 'realization',       label: 'Реализация ₽',  right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization),
      tooltip: 'Реализация = выручка за вычетом возвратов' },
    { key: 'realization_share', label: '% реализации',  right: true, render: (r) => pct(r.realization_share),   csvRender: (r) => (r.realization_share * 100).toFixed(1),
      tooltip: 'Доля реализации сегмента от общей реализации' },
    { key: 'rows',              label: 'Строк',         right: true, render: (r) => num(r.rows),
      tooltip: 'Количество строк реализации (отдельных позиций) по покупателям сегмента' },
  ];
  return (
    <>
      <TabDesc tabKey="segments" />
      <SortableTable cols={cols} rows={data} filename="segments.csv" />
    </>
  );
}

function ProductsTab({ data }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() =>
    search
      ? filterBySearch(data, search)
      : data,
    [data, search]
  );

  // ABCD классификация по 4 метрикам — по всей data, чтобы шкала не плавала при поиске
  const { getRevAbcd, getRepeatAbcd, getSelfAbcd, getCrossAbcd } = useMemo(() => {
    const totalRev = data.reduce((s, r) => s + (r.revenue || 0), 0);
    const relevant = data.filter((r) => totalRev > 0 && (r.revenue || 0) / totalRev >= 0.01);
    const relevSet = new Set(relevant);

    const revFn   = abcdByCumulative(data,     (r) => r.revenue    || 0);
    const repFn   = abcdByCumulative(relevant, (r) => r.repeat_pct || 0);
    const selfFn  = abcdByCumulative(relevant, (r) => r.self_pct   || 0);
    const crossFn = abcdByCumulative(relevant, (r) => r.cross_pct  || 0);

    return {
      getRevAbcd:   (r) => revFn(r),
      getRepeatAbcd:(r) => relevSet.has(r) ? repFn(r)   : null,
      getSelfAbcd:  (r) => relevSet.has(r) ? selfFn(r)  : null,
      getCrossAbcd: (r) => relevSet.has(r) ? crossFn(r) : null,
    };
  }, [data]);

  const cols = [
    // 4 компактных ABCD-колонки
    { key: '_abcd_rev',   label: 'Выр',  noSort: true, right: true,
      tooltip: 'ABCD по выручке (метод накопленного итога): A — товары в топ-80% оборота; B — следующие 15%; C — остаток с выручкой > 0; D — нулевая выручка',
      render: (r) => abcdBadge(getRevAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRevAbcd(r)]    || '' }),
      csvRender: (r) => getRevAbcd(r) },
    { key: '_abcd_rep',   label: 'Пов%', noSort: true, right: true,
      tooltip: 'ABCD по % повторных заказо-дней (накопленный итог): A — товары с наибольшим суммарным вкладом в повторные продажи. Только товары ≥1% оборота; нерелевантные = «—»',
      render: (r) => abcdBadge(getRepeatAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRepeatAbcd(r)] || '' }),
      csvRender: (r) => getRepeatAbcd(r) || '' },
    { key: '_abcd_self',  label: 'Себя', noSort: true, right: true,
      tooltip: 'ABCD по самоповторам: доля заказо-дней, где покупатель вернулся за тем же товаром. A — наибольший суммарный вклад. Только товары ≥1% оборота',
      render: (r) => abcdBadge(getSelfAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getSelfAbcd(r)]   || '' }),
      csvRender: (r) => getSelfAbcd(r) || '' },
    { key: '_abcd_cross', label: 'Кросс',noSort: true, right: true,
      tooltip: 'ABCD по кросс-продажам: доля заказо-дней, где покупатель взял другой товар. A — наибольший суммарный вклад. Только товары ≥1% оборота',
      render: (r) => abcdBadge(getCrossAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getCrossAbcd(r)]  || '' }),
      csvRender: (r) => getCrossAbcd(r) || '' },
    { key: 'name',               label: 'Товар', wrap: true, noSort: true, render: (r) => r.name || r.offer_id || r.sku, csvRender: (r) => r.name || r.offer_id || r.sku },
    { key: 'revenue',            label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),        csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка по всем заказам с этим товаром',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRevAbcd(r)] || '' }) },
    { key: 'realization',        label: 'Реализация ₽', right: true, render: (r) => rub(r.realization),    csvRender: (r) => Math.round(r.realization),
      tooltip: 'Выручка за вычетом возвратов' },
    { key: 'buyers',             label: 'Покупателей',  right: true, render: (r) => num(r.buyers),
      tooltip: 'Число уникальных покупателей, купивших этот товар хотя бы раз' },
    { key: 'order_days',         label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days),
      tooltip: 'Уникальных пар «покупатель + дата» с этим товаром. Единица анализа повторных покупок' },
    { key: 'repeat_order_days',  label: 'Повторных д.', right: true, render: (r) => num(r.repeat_order_days),
      tooltip: 'Из всех заказо-дней — сколько пришлось на покупателей, уже имевших покупки ранее' },
    { key: 'repeat_pct',         label: '% повторов',   right: true, render: (r) => pct(r.repeat_pct),     csvRender: (r) => (r.repeat_pct * 100).toFixed(1),
      tooltip: 'Доля повторных заказо-дней: repeat_order_days / order_days',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRepeatAbcd(r)] || '' }) },
    { key: 'self_pct',           label: 'Себя %',       right: true, render: (r) => pct(r.self_pct),       csvRender: (r) => (r.self_pct * 100).toFixed(1),
      tooltip: 'Из повторных заказо-дней — доля, где покупатель купил именно этот товар снова',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getSelfAbcd(r)]   || '' }) },
    { key: 'cross_pct',          label: 'Кросс %',      right: true, render: (r) => pct(r.cross_pct),      csvRender: (r) => (r.cross_pct * 100).toFixed(1),
      tooltip: 'Из повторных заказо-дней — доля, где покупатель купил другой товар (кросс-продажа)',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getCrossAbcd(r)]  || '' }) },
    { key: 'both_pct',           label: 'Оба %',        right: true, render: (r) => pct(r.both_pct),       csvRender: (r) => (r.both_pct * 100).toFixed(1),
      tooltip: 'Доля заказо-дней, где покупатель взял и этот товар, и другие товары одновременно' },
  ];

  return (
    <>
      <TabDesc tabKey="products" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Поиск по товару / offer_id / SKU..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 300, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} из {data.length} товаров</span>
      </div>
      <SortableTable cols={cols} rows={filtered} filename="products_assortment.csv" pageSize={100} />
    </>
  );
}

function FirstProductTab({ data }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() =>
    search
      ? filterBySearch(data, search)
      : data,
    [data, search]
  );

  // ABCD по 2 метрикам: доля покупателей (cumulative) + cross_pct (quartile, relevant only)
  const { getBuyersAbcd, getFpCrossAbcd } = useMemo(() => {
    const totalRev = data.reduce((s, r) => s + (r.revenue || 0), 0);
    const relevant = data.filter((r) => totalRev > 0 && (r.revenue || 0) / totalRev >= 0.01);
    const relevSet = new Set(relevant);

    const buyersFn = abcdByCumulative(data,     (r) => r.buyers    || 0);
    const crossFn  = abcdByCumulative(relevant, (r) => r.cross_pct || 0);

    return {
      getBuyersAbcd: (r) => buyersFn(r),
      getFpCrossAbcd:(r) => relevSet.has(r) ? crossFn(r) : null,
    };
  }, [data]);

  const cols = [
    { key: '_abcd_buyers', label: 'Пок%', noSort: true, right: true,
      tooltip: 'ABCD по числу привлечённых покупателей (накопленный итог): A — первые товары, через которые пришло 80% всех покупателей',
      render: (r) => abcdBadge(getBuyersAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getBuyersAbcd(r)]  || '' }),
      csvRender: (r) => getBuyersAbcd(r) || '' },
    { key: '_abcd_cross',  label: 'Кросс',noSort: true, right: true,
      tooltip: 'ABCD по кросс-покупкам после первого товара: A — товары, после которых чаще всего покупают другое. Только товары ≥1% оборота',
      render: (r) => abcdBadge(getFpCrossAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getFpCrossAbcd(r)] || '' }),
      csvRender: (r) => getFpCrossAbcd(r) || '' },
    { key: 'name',        label: 'Первый товар',  wrap: true, noSort: true, render: (r) => r.name || r.sku, csvRender: (r) => r.name || r.sku,
      tooltip: 'Товар, который покупатель приобрёл первым в истории своих заказов' },
    { key: 'buyers',      label: 'Покупателей',   right: true, render: (r) => num(r.buyers),
      tooltip: 'Число покупателей, у которых этот товар был первой покупкой',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getBuyersAbcd(r)] || '' }) },
    { key: 'order_days',  label: 'Заказо-дней',   right: true, render: (r) => num(r.order_days),
      tooltip: 'Суммарных заказо-дней покупателей из этой группы за весь период' },
    { key: 'revenue',     label: 'Выручка ₽',     right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка от покупателей, чья первая покупка — этот товар' },
    { key: 'realization', label: 'Реализация ₽',  right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization),
      tooltip: 'Реализация (выручка минус возвраты) от этой группы покупателей' },
    { key: 'repeat_days', label: 'Повторных д.',  right: true, render: (r) => num(r.repeat_days),
      tooltip: 'Заказо-дней, когда покупатель делал повторную покупку (любого товара)' },
    { key: 'self_days',   label: 'Снова этот',    right: true, render: (r) => `${num(r.self_days)} (${pct(r.self_pct)})`,   csvRender: (r) => r.self_days,
      tooltip: 'Повторных заказо-дней, где покупатель купил тот же первый товар. В скобках — % от повторных заказо-дней' },
    { key: 'cross_days',  label: 'Другой товар',  right: true, render: (r) => `${num(r.cross_days)} (${pct(r.cross_pct)})`, csvRender: (r) => r.cross_days,
      tooltip: 'Повторных заказо-дней, где покупатель купил другой товар (кросс-продажа). В скобках — % от повторных',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getFpCrossAbcd(r)] || '' }) },
  ];

  return (
    <>
      <TabDesc tabKey="first_product" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Поиск по названию / SKU..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 280, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} из {data.length}</span>
      </div>
      <SortableTable cols={cols} rows={filtered} filename="first_product.csv" pageSize={100} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerModal — заказы покупателя + чат
// ─────────────────────────────────────────────────────────────────────────────

function CustomerModal({ customerId, accountId, request, onClose }) {
  const [orders,    setOrders]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const { chat, startChat, sendMessage, reset: resetChat } = useOzonChat({ request, accountId });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getCustomerOrders({ request, accountId, customerId });
        if (!cancelled) setOrders(d);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [request, accountId, customerId]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface, #fff)', borderRadius: 10, padding: 24,
        width: '100%', maxWidth: 720, maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Покупатель {customerId}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {loading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Загрузка заказов…</p>}
        {error   && <p style={{ color: '#e74c3c', fontSize: 13 }}>Ошибка: {error}</p>}

        {orders && (
          <>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>
              Всего заказов: <strong>{orders.total_postings}</strong>
            </p>
            {orders.postings.length === 0 && (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Заказов не найдено.</p>
            )}
            {orders.postings.map((p, i) => {
              const isActiveChatPosting = chat?.postingNumber === p.posting_number;
              return (
                <div key={p.posting_number} style={{
                  border: '1px solid var(--border, #ddd)', borderRadius: 8,
                  padding: '10px 14px', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {p.posting_number}
                        {p.created_date && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>{p.created_date}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        Выручка: <strong>{rub(p.revenue)}</strong> · Реализация: <strong>{rub(p.realization)}</strong>
                      </div>
                      {p.items.slice(0, 3).map((it, j) => (
                        <div key={j} style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                          — {it.product_name || it.offer_id} ×{it.quantity}
                        </div>
                      ))}
                      {p.items.length > 3 && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>+ ещё {p.items.length - 3} позиций</div>
                      )}
                    </div>
                    {i === 0 && (!chat || chat.step === 'start') && (
                      <ChatWidget
                        chat={chat}
                        postingNumber={p.posting_number}
                        onStart={startChat}
                        onSend={sendMessage}
                        onCancel={resetChat}
                      />
                    )}
                  </div>

                  {/* Панель написания сообщения — внутри карточки заказа */}
                  {i === 0 && isActiveChatPosting && chat?.step !== 'start' && (
                    <ChatWidget
                      chat={chat}
                      postingNumber={p.posting_number}
                      onStart={startChat}
                      onSend={sendMessage}
                      onCancel={resetChat}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function CustomersTab({ data, accountId, request, initialCustomerId }) {
  const [search,           setSearch]           = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomerId || null);

  useEffect(() => {
    if (initialCustomerId) setSelectedCustomer(initialCustomerId);
  }, [initialCustomerId]);

  const filtered = useMemo(() =>
    filterBySearch(data, search),
    [data, search]
  );

  const cols = [
    {
      key: 'customer_id', label: 'ID покупателя',
      render: (r) => (
        <button type="button"
          onClick={() => setSelectedCustomer(r.customer_id)}
          style={{ background: 'none', border: 'none', color: 'var(--link, #2980b9)', cursor: 'pointer', padding: 0, fontSize: 13, textDecoration: 'underline' }}>
          {r.customer_id}
        </button>
      ),
      csvRender: (r) => r.customer_id,
    },
    { key: 'order_days',  label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days) },
    { key: 'revenue',     label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue) },
    { key: 'realization', label: 'Реализация ₽', right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization) },
    { key: 'first_sku',   label: 'Первый SKU' },
  ];

  return (
    <>
      {selectedCustomer && (
        <CustomerModal
          customerId={selectedCustomer}
          accountId={accountId}
          request={request}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
      <TabDesc tabKey="customers" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Поиск по ID покупателя..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 260, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} повторных покупателей</span>
      </div>
      <SortableTable cols={cols} rows={filtered} filename="repeat_customers.csv" pageSize={100} />
    </>
  );
}

const PATH_CHAR_THRESHOLD = 200;

function PathsTab({ data }) {
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const cols = [
    { key: 'buyers',      label: 'Покупателей',  right: true, render: (r) => num(r.buyers) },
    { key: 'revenue',     label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue) },
    { key: 'realization', label: 'Реализация ₽', right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization) },
    {
      key: 'name_path', label: 'Путь (товары)', wrap: true, noSort: true,
      render: (r) => {
        const text = r.name_path || '—';
        if (text.length <= PATH_CHAR_THRESHOLD) return text;
        const rowKey = r.sku_path || r.offer_path || text;
        const isExp = expanded.has(rowKey);
        return (
          <span>
            {isExp ? text : text.slice(0, PATH_CHAR_THRESHOLD) + '…'}
            <button
              type="button"
              onClick={() => toggleExpand(rowKey)}
              title={isExp ? 'Свернуть' : 'Показать полностью'}
              style={{
                marginLeft: 4, background: 'none', border: 'none', padding: '0 2px',
                cursor: 'pointer', color: 'var(--link, #2980b9)', fontSize: 12, verticalAlign: 'middle',
              }}>
              {isExp ? '▲' : '▼'}
            </button>
          </span>
        );
      },
    },
    { key: 'offer_path', label: 'Путь (артикулы)', wrap: true, noSort: true },
  ];
  return (
    <>
      <TabDesc tabKey="paths" />
      <SortableTable cols={cols} rows={data} emptyText="Нет повторных покупателей с данными о товарах." filename="purchase_paths.csv" pageSize={100} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PostingsTab — raw realization rows, server-side pagination
// ─────────────────────────────────────────────────────────────────────────────

function PostingsTab({ accountId, request }) {
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const d = await listRealizationPostings({ request, accountId, page, perPage: 100, search: debouncedSearch });
      setData(d);
    } catch (e) {
      setError('Не удалось загрузить реализацию: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [request, accountId, page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'created_date',             label: 'Дата' },
    { key: 'posting_number',           label: 'Заказ' },
    { key: 'offer_id',                 label: 'Артикул' },
    { key: 'product_name',             label: 'Товар', wrap: true, noSort: true },
    { key: 'quantity',                 label: 'Кол-во', right: true },
    { key: 'seller_price_per_instance', label: 'Цена прод. ₽', right: true, render: (r) => rub(r.seller_price_per_instance), csvRender: (r) => r.seller_price_per_instance },
    { key: 'amount',                   label: 'Реализация ₽', right: true, render: (r) => rub(r.amount), csvRender: (r) => r.amount },
    { key: 'total',                    label: 'Итого ₽',      right: true, render: (r) => rub(r.total),  csvRender: (r) => r.total },
    { key: 'sku_id',                   label: 'SKU' },
    { key: 'barcode',                  label: 'Barcode' },
  ];

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 0;

  const handleCsvDownload = () => {
    if (!items.length) return;
    downloadCsv(cols, items, 'realization_postings.csv');
  };

  return (
    <>
      <TabDesc tabKey="postings" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text"
          placeholder="Поиск по заказу / артикулу / названию..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 300, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {loading ? 'Загрузка...' : `${total.toLocaleString('ru-RU')} строк`}
        </span>
        <button className="btn btn--sm" onClick={handleCsvDownload} disabled={!items.length} style={{ fontSize: 12, marginLeft: 'auto' }}>↓ CSV страницы</button>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', marginBottom: 8, fontSize: 13 }}>
          <span className="spinner" aria-hidden="true" /> Загрузка...
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key}
                    style={{ padding: '6px 10px', textAlign: c.right ? 'right' : 'left',
                      background: 'var(--glass-80, #f0f2f5)', whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--border)' }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                  {cols.map((c) => (
                    <td key={c.key}
                      style={{ padding: '5px 10px', textAlign: c.right ? 'right' : 'left',
                        whiteSpace: c.wrap ? 'normal' : 'nowrap', maxWidth: c.wrap ? 260 : undefined }}>
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && <p style={{ color: '#e74c3c', fontSize: 13 }}>{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {debouncedSearch ? 'Ничего не найдено.' : 'Нет данных. Выполните синхронизацию.'}
        </p>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" onClick={() => setPage(1)}        disabled={page <= 1}>«</button>
          <button className="btn btn--sm" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>‹</button>
          <span style={{ fontSize: 13 }}>стр. {page} из {pages}</span>
          <button className="btn btn--sm" onClick={() => setPage((p) => p + 1)} disabled={page >= pages}>›</button>
          <button className="btn btn--sm" onClick={() => setPage(pages)}    disabled={page >= pages}>»</button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function OzonRepeatOrders({ accountId, initialCustomerId }) {
  const { request, showStatus } = useApi();
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const [syncStatus,       setSyncStatus]       = useState(null);
  const [syncLoading,      setSyncLoading]      = useState(false);
  const [analyticsData,    setAnalyticsData]    = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError,   setAnalyticsError]   = useState(null);
  const [activeTab,        setActiveTab]        = useState(initialCustomerId ? 'customers' : 'postings');
  const [syncExpanded,     setSyncExpanded]     = useState(false);

  useEffect(() => {
    if (initialCustomerId) setActiveTab('customers');
  }, [initialCustomerId]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const data = await getRealizationSyncStatus({ request, accountId });
      setSyncStatus(data);
    } catch (e) {
      console.error('sync status error', e);
    }
  }, [request, accountId]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const data = await getRealizationAnalytics({ request, accountId });
      setAnalyticsData(data);
    } catch (e) {
      setAnalyticsError('Ошибка загрузки аналитики.');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [request, accountId]);

  useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);

  // Автозагрузка последнего сохранённого отчёта при открытии вкладки — это чтение
  // уже посчитанных/персистентных данных из БД (GET /analytics), а не запуск
  // синхронизации с Ozon. Раньше отчёт нужно было запрашивать вручную кнопкой
  // «Загрузить отчёты» при каждом повторном открытии вкладки, из-за чего казалось,
  // что предыдущий отчёт «пропадает». Sync остаётся полностью ручным действием.
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      await triggerRealizationSync({ request, accountId });
      showStatus('info', 'Загрузка реализаций запущена — статус обновляется сам.');
      // Опрос гасится при уходе со страницы; по окончании обновляем и отчёты.
      clearInterval(pollRef.current);
      let polls = 0;
      pollRef.current = setInterval(async () => {
        polls += 1;
        await loadSyncStatus();
        if (polls >= 100) {
          clearInterval(pollRef.current);
          loadAnalytics();
        }
      }, 3000);
    } catch (e) {
      showStatus('error', 'Не удалось запустить синхронизацию: ' + (e?.message || e));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleReset = async (year, month) => {
    if (!window.confirm(`Сбросить ${year}-${String(month).padStart(2, '0')} для повторной загрузки?`)) return;
    try {
      await resetRealizationMonth({ request, accountId, year, month });
      await loadSyncStatus();
      showStatus('success', 'Месяц сброшен — загрузится при следующей синхронизации.');
    } catch (e) {
      showStatus('error', 'Не удалось сбросить месяц: ' + (e?.message || e));
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Сбросить ВСЕ месяцы для полной повторной загрузки?\nЭто исправит ошибки дат в уже загруженных данных.')) return;
    try {
      await resetAllRealizationMonths({ request, accountId });
      await loadSyncStatus();
      showStatus('success', 'Все месяцы сброшены — загрузятся при следующей синхронизации.');
    } catch (e) {
      showStatus('error', 'Не удалось сбросить месяцы: ' + (e?.message || e));
    }
  };

  const analyticsNeeded = activeTab !== 'postings';

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Sync panel ─────────────────────────────────── */}
      <div style={{ background: 'var(--glass-80, #f5f7fa)', borderRadius: 8,
        padding: '12px 16px', marginBottom: 16, border: '1px solid var(--border-light, #e8ecf0)' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--primary" onClick={handleSync} disabled={syncLoading}>
            {syncLoading ? 'Запускаем...' : 'Синхронизировать'}
          </button>
          <button className="btn" onClick={handleResetAll} disabled={syncLoading}
            title="Сбросить все месяцы и загрузить заново (исправляет ошибки дат)">
            Сбросить все
          </button>

          {syncStatus && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {syncStatus.ok}/{syncStatus.total_months} мес. загружено
              {syncStatus.error > 0  && <span style={{ color: '#e74c3c', marginLeft: 8 }}>· {syncStatus.error} ошибок</span>}
              {syncStatus.pending > 0 && <span style={{ color: '#f39c12', marginLeft: 8 }}>· {syncStatus.pending} ожидают</span>}
            </span>
          )}

          {syncStatus && (
            <button type="button"
              style={{ background: 'none', border: 'none', color: 'var(--link, #2980b9)', cursor: 'pointer', fontSize: 12, padding: 0 }}
              onClick={() => setSyncExpanded((v) => !v)}>
              {syncExpanded ? 'Скрыть ▲' : 'Детали ▼'}
            </button>
          )}

          {analyticsNeeded && (
            <button className="btn btn--secondary" onClick={loadAnalytics} disabled={analyticsLoading}
              style={{ marginLeft: 'auto' }}>
              {analyticsLoading ? 'Вычисляем...' : analyticsData ? 'Обновить отчёты' : 'Загрузить отчёты'}
            </button>
          )}
        </div>

        {syncExpanded && syncStatus && (
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {syncStatus.months.map((m) => (
              <div key={m.period}
                title={m.error_message ? `Ошибка: ${m.error_message}` : m.status}
                onClick={() => m.status === 'ok' && handleReset(m.year, m.month)}
                style={{
                  fontSize: 12, padding: '2px 7px', borderRadius: 4,
                  background: (STATUS_COLOR[m.status] || '#999') + '22',
                  border: `1px solid ${STATUS_COLOR[m.status] || '#999'}`,
                  color: STATUS_COLOR[m.status] || '#999',
                  cursor: m.status === 'ok' ? 'pointer' : 'default', userSelect: 'none',
                }}>
                {m.period}{m.rows_count > 0 ? ` (${m.rows_count.toLocaleString('ru-RU')})` : ''}
              </div>
            ))}
            <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 4 }}>
              Клик по ✓ месяцу — сбросить для повтора
            </span>
          </div>
        )}
      </div>

      {/* ── Errors / loading ─────────────────────────── */}
      {analyticsError && (
        <div style={{ color: '#e74c3c', marginBottom: 12, fontSize: 13 }}>{analyticsError}</div>
      )}
      {analyticsLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', marginBottom: 12 }}>
          <span className="spinner" aria-hidden="true" /> Вычисляем аналитику...
        </div>
      )}

      {/* ── Tab nav ──────────────────────────────────── */}
      <div className="account-tabs account-tabs--secondary" style={{ marginBottom: 16 }}>
        {REPORT_TABS.map((t) => (
          <button key={t.key} type="button"
            className={activeTab === t.key ? 'active' : ''}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Postings tab (always works) ───────────────── */}
      {activeTab === 'postings' && (
        <PostingsTab accountId={accountId} request={request} />
      )}

      {/* ── Analytics tabs ───────────────────────────── */}
      {activeTab !== 'postings' && !analyticsData && !analyticsLoading && !analyticsError && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Отчёт ещё не загружался. Нажмите «Обновить отчёты».
        </div>
      )}

      {activeTab !== 'postings' && analyticsData && !analyticsData.has_data && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Нет данных. Выполните синхронизацию и повторите загрузку отчётов.
        </div>
      )}

      {activeTab !== 'postings' && analyticsData && analyticsData.has_data && (
        <>
          {activeTab === 'summary'       && <SummaryTab data={analyticsData.summary} />}
          {activeTab === 'dynamics'      && <DynamicsTab dynamics={analyticsData.dynamics} daily={analyticsData.daily} />}
          {activeTab === 'segments'      && <SegmentsTab data={analyticsData.segments} />}
          {activeTab === 'products'      && <ProductsTab data={analyticsData.products} />}
          {activeTab === 'first_product' && <FirstProductTab data={analyticsData.first_product} />}
          {activeTab === 'customers'     && <CustomersTab data={analyticsData.customers} accountId={accountId} request={request} initialCustomerId={initialCustomerId} />}
          {activeTab === 'paths'         && <PathsTab data={analyticsData.paths} />}
        </>
      )}
    </div>
  );
}
